import React, { useState } from 'react';
import {
  Tag, Space, Input, Select, Row, Col,
  Button, Modal, Form, message,
} from 'antd';
import {
  EyeOutlined, PlusOutlined, EditOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCustomers } from '../../hooks/useCustomers';
import { DataTable } from '../../components';
import type {
  Customer, CreateCustomerDto, UpdateCustomerDto,
} from '../../services/customer.service';

interface CustomerFormValues {
  tenant_id?: string;
  name: string;
  email?: string;
  phone?: string;
  state?: number;
}

const getErrorMessage = (error: unknown, fallback: string): string => {
  const maybe = error as { response?: { data?: { message?: string } }; message?: string };
  return maybe?.response?.data?.message || maybe?.message || fallback;
};

const CustomerList: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [stateFilter, setStateFilter] = useState<number | undefined>();
  const [tenantFilter, setTenantFilter] = useState<string>('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [form] = Form.useForm<CustomerFormValues>();

  const {
    customers, loading, meta, setFilters, refetch, filters,
    createCustomer, updateCustomer, deleteCustomer,
  } = useCustomers();

  const applyFilters = (overrides: Partial<typeof filters> = {}) => {
    setFilters({
      ...filters,
      page: 1,
      q: searchText || undefined,
      state: stateFilter,
      tenant_id: tenantFilter || undefined,
      ...overrides,
    });
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applyFilters({ q: value || undefined });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters({ ...filters, page, limit: pageSize });
  };

  const openCreateModal = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEditModal = (customer: Customer) => {
    setEditing(customer);
    form.setFieldsValue({
      tenant_id: customer.tenant_id,
      name: customer.name,
      email: customer.email,
      phone: customer.phone,
      state: customer.state,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (values: CustomerFormValues) => {
    try {
      if (editing) {
        const dto: UpdateCustomerDto = {
          name: values.name,
          email: values.email,
          phone: values.phone,
          state: values.state,
        };
        await updateCustomer(editing.customer_id, dto);
        message.success('Cập nhật khách hàng thành công');
      } else {
        if (!values.tenant_id) {
          message.error('Tenant ID bắt buộc khi tạo khách hàng');
          return;
        }
        const dto: CreateCustomerDto = {
          tenant_id: values.tenant_id,
          name: values.name,
          email: values.email,
          phone: values.phone,
        };
        await createCustomer(dto);
        message.success('Tạo khách hàng thành công');
      }
      setModalOpen(false);
      form.resetFields();
    } catch (error) {
      message.error(getErrorMessage(error, 'Thao tác thất bại'));
    }
  };

  const handleDelete = (record: Customer) => {
    Modal.confirm({
      title: 'Xoá khách hàng',
      content: `Soft delete "${record.name}"? Email/SĐT sẽ được prefix deleted_<ts>_.`,
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteCustomer(record.customer_id);
          message.success('Xoá thành công');
        } catch (error) {
          message.error(getErrorMessage(error, 'Xoá thất bại'));
        }
      },
    });
  };

  const columns: ColumnsType<Customer> = [
    {
      title: 'Họ tên',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <a onClick={() => navigate(`/customers/${record.customer_id}`)}>{name || '(không tên)'}</a>
          {record.source && <span style={{ fontSize: 11, color: '#8c8c8c' }}>{record.source}</span>}
        </Space>
      ),
    },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'SĐT', dataIndex: 'phone', key: 'phone', width: 140 },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      width: 200,
      render: (id?: string) => id ? <code style={{ fontSize: 11 }}>{id.slice(-8)}</code> : '—',
    },
    {
      title: 'Trạng thái',
      dataIndex: 'state',
      key: 'state',
      width: 110,
      render: (s: number) => <Tag color={s === 1 ? 'success' : 'default'}>{s === 1 ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Ngày tạo',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (date: string) => date ? dayjs(date).format('DD/MM/YYYY') : '—',
    },
  ];

  const filterContent = (
    <Row gutter={16}>
      <Col span={8}>
        <Input.Search
          placeholder="Tenant ID"
          allowClear
          value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)}
          onSearch={() => applyFilters()}
        />
      </Col>
      <Col span={6}>
        <Select
          placeholder="Trạng thái"
          allowClear
          style={{ width: '100%' }}
          value={stateFilter}
          onChange={(v) => { setStateFilter(v); applyFilters({ state: v }); }}
          options={[
            { value: 1, label: 'Active' },
            { value: 0, label: 'Inactive' },
          ]}
        />
      </Col>
    </Row>
  );

  return (
    <>
      <DataTable<Customer>
        title="Khách hàng (cross-tenant)"
        data={customers}
        columns={columns}
        loading={loading}
        rowKey="customer_id"
        searchable
        searchPlaceholder="Tên / email / SĐT..."
        onSearch={handleSearch}
        onRefresh={refetch}
        filters={filterContent}
        pagination={{
          current: meta.page,
          pageSize: meta.limit,
          total: meta.total,
          onChange: handlePageChange,
        }}
        onRowClick={(record) => navigate(`/customers/${record.customer_id}`)}
        extraActions={
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Thêm khách hàng
          </Button>
        }
        actionColumn={{
          width: 110,
          fixed: 'right',
          actions: (record) => [
            { key: 'view', label: 'Xem', icon: <EyeOutlined />, onClick: () => navigate(`/customers/${record.customer_id}`) },
            { key: 'edit', label: 'Sửa', icon: <EditOutlined />, onClick: () => openEditModal(record) },
            { key: 'delete', label: 'Xoá', icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(record) },
          ],
        }}
      />

      <Modal
        title={editing ? 'Sửa khách hàng' : 'Thêm khách hàng'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        onOk={() => form.submit()}
        width={600}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          {!editing && (
            <Form.Item
              name="tenant_id"
              label="Tenant ID"
              rules={[{ required: true, message: 'Tenant ID bắt buộc' }]}
            >
              <Input placeholder="Mongo ObjectId của tenant" />
            </Form.Item>
          )}
          <Form.Item
            name="name"
            label="Họ tên"
            rules={[{ required: true, message: 'Họ tên bắt buộc' }]}
          >
            <Input placeholder="Tên khách hàng" />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ type: 'email', message: 'Email không hợp lệ' }]}>
            <Input placeholder="email@example.com" />
          </Form.Item>
          <Form.Item name="phone" label="Số điện thoại">
            <Input placeholder="0900000000" />
          </Form.Item>
          {editing && (
            <Form.Item name="state" label="Trạng thái">
              <Select
                options={[
                  { value: 1, label: 'Active' },
                  { value: 0, label: 'Inactive' },
                ]}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default CustomerList;
export { CustomerList };
